/* eslint-disable camelcase */
import React from "react"
import { useSelector } from "react-redux"

import { SilResultSubsystem } from "server/models"
import { LangType, StoreType, UserSettings } from "client/store"

import {
    ARCHITECTURE_1oo1,
    ARCHITECTURE_1oo2,
    ARCHITECTURE_1oo3,
    ARCHITECTURE_2oo2,
    ARCHITECTURE_2oo3,
} from "const"

import * as Const from "client/components/pdfmake/template/const"
import FormatPercentage, { FormatPercentageString } from "client/pages/sil/format/percentage"

import {
    ResultTable,
    ResultTableAnnotation,
    BlockContent,
} from "../../styled"


interface Iec615082010Props {
    subsystemList: readonly SilResultSubsystem[]
    showArchitectureConstraints: boolean
}

export const Iec615082010 = ({
    subsystemList,
    showArchitectureConstraints,
}: Iec615082010Props) => {
    const lang = useSelector((state: StoreType) => state.system.lang)
    return (
        <BlockContent>
            <ResultTable>
                <thead>
                    <tr>
                        <th>{lang.subsystem}</th>
                        <th>{lang.architecture}</th>
                        {
                            showArchitectureConstraints
                            && <th>{lang.architecturalConstraints}</th>
                        }
                        <th>{lang.t1}</th>
                        <th>{lang.mttr}</th>
                        <th>{lang.mrt}</th>
                        <th>{lang.beta}</th>
                        <th>{lang.betaD}</th>
                        <th>{lang.k}</th>
                    </tr>
                </thead>

                <tbody>
                    {
                        subsystemList.map((subsystem) => {
                            const t1 = subsystem.iec615082010t1
                            const mttr = subsystem.iec615082010mttr
                            const mrt = subsystem.iec615082010mrt
                            let beta: string | JSX.Element = <FormatPercentage data={subsystem.iec615082010beta} tableView />
                            let betaD: string | JSX.Element = <FormatPercentage data={subsystem.iec615082010betaD} tableView />
                            let k: string | JSX.Element = <FormatPercentage data={subsystem.iec615082010k} tableView />

                            if (
                                subsystem.architecture === ARCHITECTURE_1oo1
                                || subsystem.architecture === ARCHITECTURE_2oo2
                            ) {
                                beta = lang.n_a
                                betaD = lang.n_a
                                k = lang.n_a
                            }
                            if (
                                subsystem.architecture === ARCHITECTURE_1oo2
                                || subsystem.architecture === ARCHITECTURE_1oo3
                                || subsystem.architecture === ARCHITECTURE_2oo3
                            ) {
                                k = lang.n_a
                            }

                            return (
                                <tr key={subsystem.id}>
                                    <td>{subsystem.name}</td>
                                    <td>{subsystem.architecture}</td>
                                    {
                                        showArchitectureConstraints
                                        && <td>{lang[subsystem.architecturalConstraints]}</td>
                                    }
                                    <td>{t1}</td>
                                    <td>{mttr}</td>
                                    <td>{mrt}</td>
                                    <td>{beta}</td>
                                    <td>{betaD}</td>
                                    <td>{k}</td>
                                </tr>
                            )
                        })
                    }
                </tbody>
            </ResultTable>

            <ResultTableAnnotation>{`${lang.n_a} = ${lang.notApplicable}`}</ResultTableAnnotation>
        </BlockContent>
    )
}


export const Iec615082010PdfExport = (
    subsystemList: readonly SilResultSubsystem[],
    showArchitectureConstraints: boolean,
    userSettings: UserSettings,
    lang: LangType,
) => {
    const stack: any[] = []

    subsystemList.forEach((subsystem) => {
        const t1 = subsystem.iec615082010t1
        const mttr = subsystem.iec615082010mttr
        const mrt = subsystem.iec615082010mrt
        let beta = FormatPercentageString(subsystem.iec615082010beta, userSettings, lang)
        let betaD = FormatPercentageString(subsystem.iec615082010betaD, userSettings, lang)
        let k = FormatPercentageString(subsystem.iec615082010k, userSettings, lang)

        if (
            subsystem.architecture === ARCHITECTURE_1oo1
            || subsystem.architecture === ARCHITECTURE_2oo2
        ) {
            beta = null
            betaD = null
            k = null
        }
        if (
            subsystem.architecture === ARCHITECTURE_1oo2
            || subsystem.architecture === ARCHITECTURE_1oo3
            || subsystem.architecture === ARCHITECTURE_2oo3
        ) {
            k = null
        }

        const list: string[] = [
            `${lang.architecture}: ${subsystem.architecture}`,
        ]
        if (showArchitectureConstraints) {
            list.push(`${lang.architecturalConstraints}: ${lang[subsystem.architecturalConstraints]}`)
        }
        list.push(
            `${lang.t1}: ${t1} ${lang.hours}`,
            `${lang.mttr}: ${mttr} ${lang.hours}`,
            `${lang.mrt}: ${mrt} ${lang.hours}`,
        )
        if (beta !== null) list.push(`${lang.beta}: ${beta}`)
        if (betaD !== null) list.push(`${lang.betaD}: ${betaD}`)
        if (k !== null) list.push(`${lang.k}: ${k}`)

        stack.push(
            {
                bold: true,
                text: `${lang.subsystem} - ${subsystem.name}`,
                margin: Const.SUBSYSTEM_NAME_MARGIN,
            },
            {
                style: Const.LIST,
                ul: list,
            },
        )
    })

    return stack
}
